import { useState, type ReactNode } from 'react';
import type { Doc } from '../../convex/_generated/dataModel';
import { downloadSummaryPdf } from '../lib/summaryPdf';

type Summary = NonNullable<Doc<'meetings'>['summary']>;

interface Props {
  summary: Summary;
  audioFileName: string;
}

export function SummaryPanel({ summary, audioFileName }: Props) {
  const [copied, setCopied] = useState(false);

  const asMarkdown = (): string => {
    const parts: string[] = [];
    parts.push(`# ${summary.title}`, '');
    if (summary.overview) parts.push(`## Resumen ejecutivo`, '', summary.overview, '');

    if (summary.participants.length) {
      parts.push(`## Participantes`, '');
      summary.participants.forEach((p) => parts.push(`- ${p}`));
      parts.push('');
    }
    if (summary.keyPoints.length) {
      parts.push(`## Puntos clave`, '');
      summary.keyPoints.forEach((p) => parts.push(`- ${p}`));
      parts.push('');
    }
    if (summary.decisions.length) {
      parts.push(`## Decisiones`, '');
      summary.decisions.forEach((d) => parts.push(`- ${d}`));
      parts.push('');
    }
    if (summary.actionItems.length) {
      parts.push(`## Action items`, '');
      summary.actionItems.forEach((a) => {
        const due = a.dueDate ? ` _(${a.dueDate})_` : '';
        parts.push(`- **${a.owner}** — ${a.task}${due}`);
      });
      parts.push('');
    }
    if (summary.risks.length) {
      parts.push(`## Riesgos / Pendientes`, '');
      summary.risks.forEach((r) => parts.push(`- ${r}`));
      parts.push('');
    }
    if (summary.nextSteps.length) {
      parts.push(`## Siguientes pasos`, '');
      summary.nextSteps.forEach((n) => parts.push(`- ${n}`));
      parts.push('');
    }
    return parts.join('\n');
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(asMarkdown());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  const onDownload = () => {
    const blob = new Blob([asMarkdown()], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${audioFileName.replace(/\.[^/.]+$/, '')}-resumen.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onDownloadPdf = () => {
    void downloadSummaryPdf(summary, audioFileName);
  };

  return (
    <section className="reveal-up" style={{ animationDelay: '180ms' }}>
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
        <p className="label-mono">
          <span className="text-[var(--color-amber-mid)]">[05]</span> · summary.exec()
          <span className="text-[var(--color-asphalt-300)]"> // ai_extract</span>
        </p>
        <div className="flex items-center gap-2 no-print">
          <ActionButton onClick={onCopy} label={copied ? '✓ copied' : 'cp md'} />
          <ActionButton onClick={onDownload} label="wget .md" />
          <ActionButton onClick={onDownloadPdf} label="wget .pdf" />
        </div>
      </div>

      <article className="term-box p-6 sm:p-10 space-y-10 relative z-10">
        <header>
          <p className="label-mono mb-2">
            <span className="text-[var(--color-amber-mid)]">{'>'}</span> /summary/title
          </p>
          <h2 className="heading-display text-4xl sm:text-5xl text-[var(--color-asphalt)] mb-4 break-words">
            {summary.title}
          </h2>
          {summary.overview && (
            <p className="text-[17px] leading-[1.65] text-[var(--color-asphalt-700)] max-w-3xl break-words">
              {summary.overview}
            </p>
          )}
        </header>

        {summary.participants.length > 0 && (
          <Section label="Participantes" count={summary.participants.length}>
            <div className="flex flex-wrap gap-2">
              {summary.participants.map((p, i) => (
                <span
                  key={i}
                  className="px-3 py-1 max-w-full bg-[var(--color-bone-100)] border border-[var(--color-asphalt-300)] font-mono text-xs text-[var(--color-asphalt)] break-words"
                >
                  {p}
                </span>
              ))}
            </div>
          </Section>
        )}

        {summary.keyPoints.length > 0 && (
          <Section label="Puntos clave" count={summary.keyPoints.length}>
            <ul className="space-y-3">
              {summary.keyPoints.map((p, i) => (
                <NumberedItem key={i} idx={i + 1}>
                  {p}
                </NumberedItem>
              ))}
            </ul>
          </Section>
        )}

        {summary.decisions.length > 0 && (
          <Section label="Decisiones" count={summary.decisions.length}>
            <ul className="space-y-3">
              {summary.decisions.map((d, i) => (
                <li key={i} className="flex gap-4 min-w-0">
                  <span className="font-mono text-[var(--color-amber-deep)] text-sm pt-1 shrink-0">
                    ◆
                  </span>
                  <p className="text-[15px] leading-[1.6] text-[var(--color-asphalt)] min-w-0 flex-1 break-anywhere">
                    {d}
                  </p>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {summary.actionItems.length > 0 && (
          <Section label="Action items" count={summary.actionItems.length}>
            <div className="hidden sm:grid sm:grid-cols-[minmax(140px,160px)_1fr_minmax(100px,120px)] gap-x-5 gap-y-1 pb-2 border-b border-[var(--color-asphalt-300)]">
              <p className="label-mono">Responsable</p>
              <p className="label-mono">Tarea</p>
              <p className="label-mono">Fecha</p>
            </div>
            <div className="divide-y divide-[var(--color-bone-300)]">
              {summary.actionItems.map((a, i) => (
                <div
                  key={i}
                  className="grid grid-cols-1 sm:grid-cols-[minmax(140px,160px)_1fr_minmax(100px,120px)] gap-x-5 gap-y-2 py-4 first:pt-3 last:pb-0"
                >
                  <div className="sm:contents">
                    <p className="label-mono sm:hidden mb-0.5">Responsable</p>
                    <p className="font-mono text-xs text-[var(--color-amber-deep)] uppercase tracking-wide break-words">
                      {a.owner}
                    </p>
                  </div>
                  <div className="sm:contents">
                    <p className="label-mono sm:hidden mb-0.5">Tarea</p>
                    <p className="text-[15px] leading-[1.55] text-[var(--color-asphalt)] break-anywhere">
                      {a.task}
                    </p>
                  </div>
                  <div className="sm:contents">
                    <p className="label-mono sm:hidden mb-0.5">Fecha</p>
                    <p className="font-mono text-xs text-[var(--color-asphalt-500)] nums-tabular break-words">
                      {a.dueDate ?? '—'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {summary.risks.length > 0 && (
          <Section label="Riesgos & pendientes" count={summary.risks.length}>
            <ul className="space-y-3">
              {summary.risks.map((r, i) => (
                <li key={i} className="flex gap-4 min-w-0">
                  <span className="font-mono text-[var(--color-burgundy)] text-sm pt-1 shrink-0">
                    !
                  </span>
                  <p className="text-[15px] leading-[1.6] text-[var(--color-asphalt-700)] min-w-0 flex-1 break-anywhere">
                    {r}
                  </p>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {summary.nextSteps.length > 0 && (
          <Section label="Siguientes pasos" count={summary.nextSteps.length}>
            <ul className="space-y-3">
              {summary.nextSteps.map((n, i) => (
                <NumberedItem key={i} idx={i + 1}>
                  {n}
                </NumberedItem>
              ))}
            </ul>
          </Section>
        )}
      </article>
    </section>
  );
}

function Section({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-4">
        <p className="label-mono">{label}</p>
        <span className="font-mono text-xs text-[var(--color-asphalt-300)] nums-tabular">
          {String(count).padStart(2, '0')}
        </span>
        <div className="flex-1 h-px bg-[var(--color-asphalt-300)]" />
      </div>
      {children}
    </section>
  );
}

function NumberedItem({ idx, children }: { idx: number; children: ReactNode }) {
  return (
    <li className="flex gap-4 min-w-0">
      <span className="font-mono text-[11px] text-[var(--color-asphalt-300)] pt-1.5 nums-tabular shrink-0 w-6">
        {String(idx).padStart(2, '0')}
      </span>
      <p className="text-[15px] leading-[1.6] text-[var(--color-asphalt)] flex-1 min-w-0 break-anywhere">
        {children}
      </p>
    </li>
  );
}

function ActionButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} className="term-btn">
      {label}
    </button>
  );
}
